import { Action } from '@reduxjs/toolkit';
import AppProviders from 'components/AppProviders/AppProviders.component';
import ReactDOM from 'react-dom/client';
import { initialize as reactLocalizeReduxInitialize } from 'react-localize-redux';
import { loadLabels } from 'services/i18n/i18n.helpers';
import { setFromOAuth } from 'services/oauth/oauth.actions';
import { checkComingFromOAuth } from 'services/oauth/oauth.helpers';
import { promoCodeQueryPrams } from 'services/recharges/recharges.actions';
import { addSessionDataInfo } from 'services/session/session.actions';
import {
  cleanAuthTokenQueryParams,
  devOnlySetupEnvironment,
  inferAuthTypeFromAuthToken,
  maybeSetSessionId,
  setAuthType,
} from 'services/session/session.helpers';
import {
  getRechargePromocode,
  getSessionInfo,
} from 'services/session/session.requests';
import configureStore from 'store/configureStore';
import { updateCurrentStore } from 'store/currentStore';
import { AppStore } from 'store/types';
import { grid, theme } from 'theme';
import 'typeface-muli';
import 'typeface-roboto';
import { loadAdobeLaunchScript } from 'utils/functions/adobe-launch';
import { getCookieValue } from 'utils/functions/cookie';
import { createScopedErrorHandler } from 'utils/functions/handleError';
import { loadOneTrustScripts } from 'utils/functions/oneTrustScripts';
import { loadSandsivScript } from 'utils/functions/sandsiv';
import { unregister as unregisterServicesWorker } from './serviceWorker';

function renderApplication(appRootDomId: string, store: AppStore) {
  const appRoot = ReactDOM.createRoot(
    document.getElementById(appRootDomId) as HTMLElement,
  );

  if (!appRoot) {
    throw new Error(
      `I couldn't locate the app root with id \`${appRootDomId}\``,
    );
  }

  return appRoot.render(
    <AppProviders store={store} theme={theme} grid={grid} />,
  );
}
// requested from the analytics team in DMC-10126
function setCookieSiteIdApp() {
  const cookieSiteId = getCookieValue('cookie_siteid');
  document.cookie = `cookie_siteid_app=${cookieSiteId}`;
}

async function bootApplication() {
  if (import.meta.env.DEV) {
    devOnlySetupEnvironment();
    window.addEventListener('error', createScopedErrorHandler('error'));
    window.addEventListener(
      'unhandledrejection',
      createScopedErrorHandler('unhandledrejection'),
    );
  }

  const store = configureStore();
  updateCurrentStore(store);

  const [translation, sessionInfo, rechargePromoCode] = await Promise.all([
    loadLabels(),
    getSessionInfo(),
    getRechargePromocode(),
  ]);

  if (sessionInfo.auth?.authToken) {
    setAuthType(inferAuthTypeFromAuthToken(sessionInfo.auth.authToken));
  }

  // react-localize-redux using fake defaults because now languages are handled by
  // loadVendorLabels, providing localization for different languages and vendors.
  // We should migrate away from react-localize-redux and use a more modern library
  store.dispatch(
    reactLocalizeReduxInitialize({
      languages: [{ name: 'italian', code: 'it-IT' }],
      translation,
      options: {
        renderToStaticMarkup: false,
        renderInnerHtml: true,
        defaultLanguage: 'it-IT',
      },
    }) as Action,
  );
  store.dispatch(addSessionDataInfo(sessionInfo));
  store.dispatch(promoCodeQueryPrams(rechargePromoCode));

  if (checkComingFromOAuth()) {
    store.dispatch(setFromOAuth());
  }

  loadAdobeLaunchScript();

  loadOneTrustScripts();

  maybeSetSessionId();
  cleanAuthTokenQueryParams();

  setCookieSiteIdApp();

  loadSandsivScript();

  renderApplication('root', store);
}

bootApplication()
  .catch(createScopedErrorHandler('boot-app'))
  .finally(unregisterServicesWorker);
